import { useEffect, useState } from 'react';
import { FRAME_MS } from '../../engine/games/luck';
import { HORSES, horseOdds, RACE_MS, type HorseState } from '../../engine/games/horseRacing';
import { money } from '../../format';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

/** Saddle-cloth colours, one per horse (post position 1-6). */
const SILKS = ['#d6332b', '#f3ead7', '#2f5fd0', '#e0b85e', '#3aa55c', '#1c1a17'];
const SILK_INK = ['#fff', '#1c1a17', '#fff', '#1c1a17', '#fff', '#e0b85e'];
/** A ★ second chance: the lost race runs over the engine's frames, then luck. */
const LOST_RACE_MS = 11 * FRAME_MS;
const LUCK_AT_MS = LOST_RACE_MS + 100;

/** Where each horse is at `p` (0-1 of the race): they trade places along
 *  the way, then finish where the engine placed them. */
function positions(finish: readonly number[], p: number): number[] {
  const e = 1 - (1 - p) ** 1.6;
  return finish.map((f, i) => Math.max(0, f * e + 0.05 * Math.sin(p * Math.PI * 3 + i * 1.9) * Math.sin(Math.PI * p)));
}

function Horse({ n }: { n: number }) {
  return (
    <svg viewBox="0 0 26 16" className="horse" aria-hidden="true">
      <path d="M1 9.5 C1.5 7 4 6 7 6.3 L15.5 6.3 C16.5 4.2 18.5 2 20.5 2 L23.5 3.2 L22.8 5.2 L20.4 5.8 L19.8 9.8 L18 10 L17.6 15 L16.2 15 L16 10.6 L8.6 10.6 L8 15 L6.6 15 L6.4 10.6 C4.6 10.6 3.2 11.4 2.2 13 Z" className="horse-body" />
      <rect x="9" y="5.2" width="6" height="5" rx="0.8" fill={SILKS[n]} />
      <text x="12" y="9.1" textAnchor="middle" fill={SILK_INK[n]} className="horse-num">{n + 1}</text>
    </svg>
  );
}

export function HorsesTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.horses?.state as HorseState | undefined;
  const anim = snapshot.anim;
  const luck = secondChanceFrame(snapshot);
  const lostWinner = luck ? HORSES.findIndex((h) => luck.includes(`${h.name} won`)) : -1;
  const elapsed = useAnimClock(snapshot, actions, luck ? (anim?.frames.length ?? 0) * FRAME_MS : 0);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, lostWinner >= 0 ? LUCK_AT_MS : null, () => ({ what: 'A lost race is run again.', saved: !!state?.lucky }), setBurst);

  const racing = state?.phase === 'racing';
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!racing) return;
    const t = setInterval(() => setNow(Date.now()), 40);
    return () => clearInterval(t);
  }, [racing]);

  const pick = state?.pick ?? 0;
  let pos: number[] = HORSES.map(() => 0);
  let winner: number | null = null;
  if (anim && lostWinner >= 0) {
    // The race you lost: its winner first, the rest (yours too) behind.
    const lost = HORSES.map((_, i) => (i === lostWinner ? 1 : 0.6 + ((i * 0.23 + 0.11) % 0.35)));
    const p = Math.min(1, elapsed / LOST_RACE_MS);
    pos = positions(lost, p);
    if (p >= 1) winner = lostWinner;
  } else if (state && racing) {
    pos = positions(state.finish, Math.min(1, Math.max(0, (now - state.startedAt) / RACE_MS)));
  } else if (state?.phase === 'done') {
    pos = state.finish;
    winner = state.winner;
  }

  const choose = (i: number) => {
    if (!state || racing || anim || i === pick) return;
    const steps = (i - pick + HORSES.length) % HORSES.length;
    for (let k = 0; k < steps; k++) actions.tableOption('horse');
  };

  let result: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (state?.phase === 'done' && !anim) {
    const net = state.returned - state.bet;
    result = `${HORSES[state.winner]!.name} wins · ${net > 0 ? `${state.lucky ? '★ ' : ''}+${money(net)}` : `your ${HORSES[state.pick]!.name} −${money(state.bet)}`}`;
    tone = net > 0 ? 'win' : 'loss';
  }

  return (
    <>
      <Stage className="horses-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        <div className="tote" role="radiogroup" aria-label="Pick a horse">
          <div className="tote-head"><span>#</span><span>Horse</span><span>Odds</span></div>
          {HORSES.map((h, i) => (
            <button key={h.name} role="radio" aria-checked={pick === i} className={`tote-row${pick === i ? ' on' : ''}${winner === i ? ' won' : ''}`} onClick={() => choose(i)} disabled={racing || !!anim}>
              <span className="tote-post" style={{ background: SILKS[i], color: SILK_INK[i] }}>{i + 1}</span>
              <span className="tote-name">{h.name}</span>
              <span className="tote-odds">{horseOdds(i).toFixed(1)}×</span>
            </button>
          ))}
        </div>
        <div className="track" aria-label={winner !== null ? `${HORSES[winner]!.name} won` : 'The track'}>
          {HORSES.map((h, i) => (
            <div key={h.name} className={`lane${pick === i ? ' yours' : ''}`}>
              <span className="lane-runner" style={{ left: `calc((100% - var(--horse-w)) * ${Math.min(1, pos[i]!).toFixed(4)})` }}>
                <Horse n={i} />
              </span>
            </div>
          ))}
          <span className="finish-line" aria-hidden="true" />
        </div>
        <StatusLine snapshot={snapshot} result={result} tone={tone} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} hide={['horse', 'watch']} onAct={() => setBurst(null)} />
    </>
  );
}
